import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import { Media, UploadState } from '@synergy/models';
import { ReactElement, useEffect, useState } from 'react';

import { PoolButton } from '../buttons/pool-button/pool-button';
import { PoolDropzone } from '../pool-dropzone/pool-dropzone';
import { PoolFileList } from '../pool-file-list/pool-file-list';
import { PoolModal } from '../pool-modal/pool-modal';
import { PoolModalActions } from '../pool-modal';
import { PoolModalContent } from '../pool-modal/components/pool-modal-content';
import { PoolModalTitle } from '../pool-modal/components/pool-modal-title';

interface PoolAttachmentsModalProps {
    downloadBaseUrl: string;
    files: Media[];
    open: boolean;
    readOnly?: boolean;
    hideActions?: boolean;
    onClose: () => void;
    onSave: (uploadedFiles: File[], deleteFileIds: number[]) => void;
}

export const PoolAttachmentsModal = ({
    open,
    onClose,
    onSave,
    readOnly = false,
    hideActions = false,
    files: initialFiles,
    downloadBaseUrl,
}: PoolAttachmentsModalProps): ReactElement => {
    const [files, setFiles] = useState<Media[]>(initialFiles);
    const [deleteFilesIds, setDeleteFilesIds] = useState<number[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    useEffect(() => {
        setFiles(initialFiles);
        setDeleteFilesIds([]);
        setUploadedFiles([]);
    }, [initialFiles]);

    const handleDrop = (acceptedFiles: File[]): void => {
        const deduplicated = acceptedFiles.filter(
            file => !files.some(v => v.originalName === file.name && v.size === file.size)
        );

        setFiles([
            ...files,
            ...deduplicated.map(file => {
                const media = Media.fromFile(file);

                media.state = UploadState.Finished;

                return media;
            }),
        ]);
        setUploadedFiles([...uploadedFiles, ...deduplicated]);
    };

    const handleSave = (): void => {
        onSave(uploadedFiles, deleteFilesIds);
        // Handed on, so no longer pending: kept, a second save sent the same
        // files again (R-199).
        setUploadedFiles([]);
        setDeleteFilesIds([]);
        onClose();
    };

    const handleFileRemove = (media: Media): void => {
        if (media.id === -1) {
            setUploadedFiles(uploadedFiles.filter(v => JSON.stringify(v) !== JSON.stringify(media.file)));
            setFiles(files.filter(v => JSON.stringify(v) !== JSON.stringify(media)));
        } else {
            setDeleteFilesIds([...deleteFilesIds, media.id]);
            setFiles(files.filter(v => v.id !== media.id));
        }
    };

    const handleClose = (): void => {
        setDeleteFilesIds([]);
        setFiles(initialFiles);
        onClose();
    };

    const acceptedFiles = files.map((file: Media) => ({
        ...file,
        state: UploadState.Finished,
    }));

    return (
        <PoolModal open={open} onClose={handleClose}>
            <PoolModalTitle>
                <FormattedMessage id="components.attachmentsModal.attachedFiles" />
            </PoolModalTitle>
            <PoolModalContent>
                <Grid container={true} spacing={3}>
                    {!readOnly && (
                        <Grid item={true} xs={12}>
                            <PoolDropzone onDrop={handleDrop} />
                        </Grid>
                    )}
                    {acceptedFiles.length > 0 && (
                        <Grid item={true} xs={12}>
                            <PoolFileList
                                acceptedFiles={files.map((file: Media) => ({
                                    ...file,
                                    state: UploadState.Finished,
                                }))}
                                onDeleteClicked={handleFileRemove}
                                downloadBaseUrl={downloadBaseUrl}
                                directDownload={false}
                                readOnly={readOnly || false}
                            />
                        </Grid>
                    )}
                </Grid>
            </PoolModalContent>

            {!hideActions && (
                <PoolModalActions>
                    <PoolButton variant="outlined" onClick={handleClose}>
                        <FormattedMessage id="app.common.cancel" />
                    </PoolButton>
                    <PoolButton onClick={handleSave} disabled={readOnly || false}>
                        <FormattedMessage id="app.common.save" />
                    </PoolButton>
                </PoolModalActions>
            )}
        </PoolModal>
    );
};
